import { Injectable } from "@angular/core";
import { CreateUserRequest, UpdateUserRequest, UserModel } from "../models/data/user.model";
import { ApiBaseUrls, ApiControllers } from "../constants/constants";
import { HttpClient } from "@angular/common/http";
import { UsersFilter } from "../filters/users.filter";
import { PaginatedListModel } from "../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../models/service/pagination-response-header.model";
import { firstValueFrom } from "rxjs";
import { AuditLog } from "../models/service/audit-log.model";
import { UserAuditLogModel } from "../models/data/user-audit-log.model";
import { AuditLogMapper } from "../utilities/audit-log-mapper.utilities";
import { ServiceResultModel } from "../models/service/service-result.model";
import { APISanitizerUtilities } from "../utilities/api-sanitizer.utilities";
import { ApiResponseUtilities } from "../utilities/api-response.utilities";
import { ServiceUtilities } from "../utilities/service.utilities";
import { PasswordResetModel } from "../models/data/password-reset.model";
import { UserBulkActions } from "../enums/bulk-actions.enums";
import { BulkActionResponse } from "../models/service/bulk-action-response.model";
import { BulkActionRequest } from "../models/service/bulk-action-request.model";
import { UsersMessagingConstants } from "../constants/messaging.constants";
import { PaymentMethodType } from "../enums/data.enums";
import { CustomerPaymentMethodsModel, SetupIntentResponse } from "../models/service/payment-method.model";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private apiPath: string = `${ApiBaseUrls.AUTHENTICATION()}${ApiControllers.USERS()}`;

  constructor(private httpClient: HttpClient) { }

  public async searchUsers(usersFilter: UsersFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<UserModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      const requestUrl = this.apiPath + `/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<UserModel[]>(requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UsersFilter>(usersFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })
      );

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<UserModel> = {
        data: <UserModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createUser(user: CreateUserRequest): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath;

      const response = await firstValueFrom(
        this.httpClient.post<UserModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CreateUserRequest>(user)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const createdUser = <UserModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: createdUser, success: true, message: UsersMessagingConstants.USERS_ADD_SUCCESS(), statusCode: response.status };
    } catch (error: any) {
      if (ApiResponseUtilities.isConflictStatusCode(error?.error?.statusCode)) {
        return <ServiceResultModel>{ success: false, message: UsersMessagingConstants.USERS_ERROR_EMAIL_IN_USE(), statusCode: error?.error?.statusCode };
      }

      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateUser(user: UpdateUserRequest, userId: number): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}`;
      const response = await firstValueFrom(
        this.httpClient.patch<UserModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateUserRequest>(user)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedUser = <UserModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: updatedUser, success: true, message: UsersMessagingConstants.USERS_PROFILE_UPDATE_SUCCESS() };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error, UsersMessagingConstants.USERS_ERROR_UPDATING_PROFILE());
    }
  }

  public async bulkActivationRequest(bulkAction: UserBulkActions, bulkActionRequest: BulkActionRequest<UsersFilter | null>): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.apiPath}/bulk/status/${bulkAction}`;

      const response = await firstValueFrom(
        this.httpClient.patch<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<UsersFilter | null>>(bulkActionRequest)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const bulkActionResponse = <BulkActionResponse>response.body?.valueOf();

      const confirmationMessageData = {bulkAction: (bulkAction === UserBulkActions.Activate) ? "activated" : "deactivated", totalAffected: bulkActionResponse.totalAffected, totalSkipped: bulkActionResponse.totalSkipped};
      const confirmationMessage = UsersMessagingConstants.USERS_BULK_ACTIVATION_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);

      return <ServiceResultModel>{ data: bulkActionResponse, success: true, message: confirmationMessage, statusCode: response.status }
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateUserStatus(userId: number, active: boolean, note?: string): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/status?active=${active}`;

      const response = await firstValueFrom(
        this.httpClient.patch<UserModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const updatedUser = <UserModel>response.body?.valueOf();

      const messageData = {actionText: (active === true) ? "activated" : "deactivated", firstName: updatedUser.firstName, lastName: updatedUser.lastName};
      const message = UsersMessagingConstants.USERS_UPDATE_STATUS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{
        data: updatedUser,
        success: true,
        message: message,
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async uploadUserAvatar(userId: number, avatar: File): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/avatar`;

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('avatar', avatar);
      formData.append('userId', userId.toString());

      const response = await firstValueFrom(
        this.httpClient.patch<UserModel>(requestUrl, formData, { observe: "response" }));

      const user = <UserModel>response.body?.valueOf();

      const messageData = {firstName: user.firstName, lastName: user.lastName};
      const message = UsersMessagingConstants.USERS_UPLOAD_USER_AVATAR_SUCCESS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{
        data: user,
        success: true,
        message: message,
        statusCode: response.status
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getUserById(userId: number): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}`;
      const response = await firstValueFrom(this.httpClient.get<UserModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));

      const user = <UserModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: user, success: true, statusCode: response.status }
    } catch (error: any) {
      // userLoadedGuard retries this call on a transient failure, which needs the transport status a
      // body-less 5xx or network error does not carry in its ProblemDetails.
      const result = ServiceUtilities.handleServiceException(error);
      result.statusCode ??= ServiceUtilities.resolveStatusCode(error);
      return result;
    }
  }

  public async deleteUser(userId: number, note?: string): Promise<ServiceResultModel<UserModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<UserModel>(
          requestUrl, 
          { 
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<string | undefined>(note)),
            observe: "response", 
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } 
          }
        ));

      const user = <UserModel>response.body?.valueOf();

      const messageData = {firstName: user.firstName, lastName: user.lastName};
      const message = UsersMessagingConstants.USERS_USER_DELETE_SUCCESS().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{ data: user, success: true, message: message, statusCode: response.status }
    }
    catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async sendUserPasswordReset(user: UserModel): Promise<ServiceResultModel<PasswordResetModel>> {
    try {
      const requestUrl = this.apiPath + `/${user.userId}/reset-pw`;

      const response = await firstValueFrom(this.httpClient.patch(requestUrl, null, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const passwordReset = <PasswordResetModel>response.body?.valueOf();

      const messageData = {firstName: user.firstName, lastName: user.lastName};
      const message = UsersMessagingConstants.USERS_PASSWORD_RESET_SUCCESSFUL().replaceValuesWithObject(messageData);

      return <ServiceResultModel>{
        data: (passwordReset) ? passwordReset : undefined,
        success: true,
        message: message
      };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error, UsersMessagingConstants.USERS_ERROR_PASSWORD_RESET());
    }
  }

  public async getAuditLogsByUserId(userId: number, skip: number, take: number, includeDetails: boolean = false): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = this.apiPath + `/${userId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(this.httpClient.get<UserAuditLogModel[]>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }));
      const mappedData = AuditLogMapper.mapUserAuditLogs(<UserAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, message: "Audit logs loaded successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updateLastLoginDate(userId: number): Promise<ServiceResultModel<void>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/last-login`;

      await firstValueFrom(
        this.httpClient.patch(
          requestUrl, null, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      return <ServiceResultModel<void>>{ success: true, message: "Last login date updated successfully." };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getPaymentMethods(userId: number): Promise<ServiceResultModel<CustomerPaymentMethodsModel>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/stripe/payment-methods`;
      const response = await firstValueFrom(
        this.httpClient.get<CustomerPaymentMethodsModel>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })
      );

      const paymentMethods = <CustomerPaymentMethodsModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: paymentMethods, success: true, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createSetupIntent(userId: number, type: PaymentMethodType): Promise<ServiceResultModel<SetupIntentResponse>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/stripe/payment-setup?type=${type}`;
      const response = await firstValueFrom(
        this.httpClient.post<SetupIntentResponse>(requestUrl, null, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })
      );

      const setupIntent = <SetupIntentResponse>response.body?.valueOf();
      return <ServiceResultModel>{ data: setupIntent, success: true, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deletePaymentMethod(userId: number, paymentMethodId: string): Promise<ServiceResultModel<boolean>> {
    try {
      const requestUrl = this.apiPath + `/${userId}/stripe/payment-methods/${paymentMethodId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<boolean>(requestUrl, { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } })
      );

      return <ServiceResultModel>{ data: true, success: true, statusCode: response.status };
    } catch (error: any) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}